<template>
  <div class="room-card" :class="expanded && 'room-card--expanded'">
    <div class="room-card__header">
      <LargeText class="room-card__title">{{ room.name }}</LargeText>
      <span v-if="remainingRooms < 10 && remainingRooms !== 0" class="room-card__remaining-rooms">{{
        translate('rdRoomsLeft', remainingRooms, remainingRooms)
      }}</span>
    </div>
    <div class="room-card__preview">
      <div class="room-card__thumbnail">
        <Thumbnail
          :id="room.id"
          :room-thumbnail="true"
          :first-sub-gallery-id="room.id"
          :thumbnail="room.defaultImage"
          :has-icon="room.hasGallery()"
          :imagePosition="imagePosition"
          v-bind="triggerAttributes"
        />
        <GalleryModal v-if="room.hasGallery()" :room="room" />
      </div>
      <div class="room-card__details">
        <div class="room-card__description">
          <LargeText class="room-card__title--mobile">{{ room.name }}</LargeText>
          <span
            v-if="remainingRooms < 10 && remainingRooms !== 0"
            class="room-card__remaining-rooms room-card__remaining-rooms--mobile"
            >{{ translate('rdRoomsLeft', remainingRooms, remainingRooms) }}</span
          >
          <span class="room-card__details-title">{{ translate('rdRoomDetails') }}</span>
          <div class="room-card__features">
            <div class="room-card__feature">
              <span class="room__feature-text">{{ translate('rdMaxGuests') }} :</span>
              <span class="room-card__feature-text">{{
                translate('boNumAdults', room.occupancy.adults, room.occupancy.adults)
              }}</span>
              <span class="room-card__feature-text" v-if="room.occupancy.children > 0"
                >,
                {{
                  translate('boNumChildren', room.occupancy.children, room.occupancy.children)
                }}&nbsp;</span
              >
            </div>
            <div class="room-card__feature" v-if="room.bedType">
              <span class="room-card__feature-text">{{ translate('rdBedType') }} :</span>
              <span>{{ translatedBedType }}</span>
            </div>
            <div class="room-card__feature" v-if="room.size">
              <span class="room__feature-text">{{ translate('rdSize') }} :</span>
              <span data-testid="room-size">{{ metricArea }}</span>
            </div>
            <div class="room-card__feature" v-if="room.isAccessible()">
              <span class="room-card__feature-text">{{
                translate('rdAccessibleFeatureAvailable')
              }}</span>
            </div>
          </div>
          <div class="room-card__services mb-2">
            <RoomCardServices
              v-for="(service, index) in roomServices"
              :key="index"
              :service="service"
            />
          </div>
          <button
            id="button-trigger-modal"
            data-testid="button-trigger-modal"
            :modal="modalId"
            type="button"
            class="room-card__button rhg-btn-link rhg-btn-link--small rhg-btn-link__uppercase rhg-btn-link__bold"
            data-gallery-mode="embedded"
            :data-gallery-element="`#my-viewer-${room.id}`"
            data-gallery-type="rooms"
            :data-gallery-type-id="`${hotelCode}|${room.id}`"
            @click="$bvModal.show(modalId)"
          >
            <span class="room-card__button-text">
              {{ translate('rdMoreRoomDetails') }}
            </span>
            <i class="rhg-font-icon--arrow-right"></i>
          </button>
        </div>
        <div class="room-card__price" v-show="currentRoom > 1">
          <FromPriceButton
            :stay="room.getCheaperStay()"
            modalTrigger
            :isLastRoomTab="isLastRoomTab"
            :currentRoom="currentRoom"
            :rooms="rooms"
            :room="room"
            @roomSelected="handleRoomSelected"
          />
        </div>
      </div>
    </div>
    <div v-if="childrenValue > 0" class="room-card__children-fee">
      <i class="xs mr-3 ml-2 icon-alert-circle icon-noticication"></i>
      <span class="text-12">{{ translate('boChildrenBedAdditionalFees') }}</span>
    </div>
    <RoomModalRooms :id="modalId" :room="room" />
  </div>
</template>

<script>
import Room from '../../domain/Room'
import Thumbnail from '../shared/Thumbnail'
import LargeText from '../shared/LargeText'
import RoomCardServices from './RoomCardServices'
import RoomModalRooms from './RoomModalRooms'
import FromPriceButton from '@/multi-room-display/components/shared/FromPriceButton'
import { getQueryParams, getRoomGalleryTriggers, getImagePosition } from '@/infrastructure/Utils'
import ExternalStorage from '@/services/ExternalStorage'
// import { timingSafeEqual } from 'crypto'

export default {
  name: 'RoomCard',
  components: {
    FromPriceButton,
    RoomCardServices,
    LargeText,
    Thumbnail,
    GalleryModal: () => import('../modal/GalleryModal'),
    RoomModalRooms
  },
  data: () => ({
    childrenValue: 0
  }),
  filters: {
    spaceInSize(size) {
      const onlyNumbers = /(\d+)/
      return size.replace(onlyNumbers, '$1 ')
    }
  },
  created() {
    const { children } = getQueryParams(window.location._search || window.location.search)
    if (children) {
      this.childrenValue = parseInt(children[0])
    }
  },
  props: {
    room: {
      type: Room
    },
    expanded: {
      type: Boolean
    },
    isLastRoomTab: {
      type: Boolean
    },
    currentRoom: {
      type: Number
    },
    rooms: {
      type: Array
    },
    hotelCode: String,
    hotelName: String,
    remainingRooms: Number
  },
  computed: {
    roomServices: function () {
      return this.room.services.slice(0, 4)
    },
    modalId: function () {
      return 'roomDetails-' + this.room.id
    },
    triggerAttributes() {
      return getRoomGalleryTriggers(this.hotelCode, this.room, this.hotelName)
    },
    translatedBedType() {
      let bedType = this.room.bedType
      if (bedType.includes(' and ')) {
        bedType = bedType.replaceAll(' and ', ` ${this.translate('weAnd')} `)
      }
      if (bedType.includes(' or ')) {
        bedType = bedType.replaceAll(' or ', ` ${this.translate('weOr')} `)
      }
      return bedType
    },
    imagePosition() {
      return getImagePosition(this.room.jsonForIntegrateWithGallery)
    },
    metricArea() {
      if (ExternalStorage.cookies.isIndia()) {
        return `${this.room.area.metres} m² | ${this.room.area.feet} ft²`
      } else {
        return `${this.room.area.metres} m²`
      }
    }
  },
  methods: {
    handleRoomSelected(
      roomSelected,
      rates,
      code,
      mealPlan,
      rate,
      mealPlanName,
      rateName,
      dealOfTheDay
    ) {
      if (roomSelected) {
        this.$emit(
          'roomSelected',
          true,
          rates,
          code,
          mealPlan,
          rate,
          mealPlanName,
          rateName,
          dealOfTheDay
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.room-card {
  background-color: white;

  .from-price {
    justify-content: space-between;
  }

  @media (min-width: 992px) {
    border-radius: 0.5rem;
    padding: 2rem;

    &--expanded {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  &__title {
    margin-bottom: 1rem;

    @media (max-width: 992px) {
      display: none;
    }

    &--mobile {
      margin: 1rem 0;

      @media (min-width: 992px) {
        display: none;
      }
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    @media (max-width: 992px) {
      display: none;
    }
  }

  &__remaining-rooms {
    font-size: 12px;
    padding: 4px 8px;
    color: white;
    background: #e17e45;
    border-radius: 14px;
    font-weight: bold;
    display: flex;
    align-items: center;
    width: fit-content;
    margin: 0 0;

    &--mobile {
      margin: 16px 0;
      @media (min-width: 992px) {
        display: none;
      }
    }
  }
  &__preview {
    display: flex;
    flex-wrap: wrap;
    @media (min-width: 992px) {
      flex-wrap: nowrap;
    }
  }

  &__thumbnail {
    min-width: 100%;
    @media (min-width: 992px) {
      width: 350px;
      min-width: 350px;
      height: 226px;
      .thumbnail {
        height: 100%;
      }
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 100%;

    @media (min-width: 992px) {
      min-width: calc(100% - 350px);
      padding-left: 2rem;
    }
  }

  &__description,
  &__price {
    padding: 2rem;

    @media (min-width: 992px) {
      padding: 0;
    }
  }

  &__price {
    border-top: 1px solid #cccccc;

    @media (min-width: 992px) {
      border: 0;
    }
  }

  &__children-fee {
    display: flex;
    margin-top: 1.6rem;
    align-items: center;
    .icon-noticication {
      transform: rotate(180deg);
      align-self: flex-start;
      font-size: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &__services {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  &__button {
    display: flex;
    gap: 4px;
    color: #c62525;
    margin-top: 10px;
    cursor: pointer;
    i {
      width: 16px;
      height: 16px;
      font-size: 16px;
    }
    &-text:hover {
      text-decoration: underline;
    }
  }

  &__features {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin-top: 1rem;
    margin-bottom: 1rem;
    gap: 10px;
  }

  &__feature {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 4px 8px;
    font-size: 12px;
    gap: 10px;
    border-radius: 35px;
    width: fit-content;
    place-items: center;
    line-height: 16px;
    background-color: rgb(238, 238, 239);
  }

  &__feature-icon {
    margin-right: 1rem;
  }

  &__feature-text {
    font-size: 12px;
    white-space: normal;
  }

  &__details-title {
    font-size: 1.4rem;
    font-weight: bold;
  }
}

html[dir='rtl'] {
  .room-card {
    &__title {
      &--mobile {
        text-align: right;
      }
      text-align: right;
    }
    &__description {
      margin-right: 2rem;
    }
    &__details-title {
      display: flex;
    }
    &__feature {
      display: flex;
    }
  }
  #button-trigger-modal {
    display: flex;
  }
}
</style>
